import i18next from "i18next";
import { initReactI18next } from "react-i18next";

import { en } from "./en.ts";
import { id } from "./id.ts";

/**
 * Bahasa Indonesia is the default and the fallback. English is there for a reviewer,
 * not for a user — if a key is missing, falling back to English is better than
 * rendering the key, but the Indonesian string is what ships.
 */
export const SUPPORTED_LANGUAGES = ["id", "en"] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

const STORAGE_KEY = "pustaka.lang";

function initialLanguage(): Language {
  // Node 25 defines a `localStorage` OBJECT without methods unless started with
  // --localstorage-file, so the `typeof localStorage` guard alone let every unit
  // test suite die at import time. Feature-detect the method, not the object.
  const stored =
    typeof localStorage !== "undefined" && typeof localStorage.getItem === "function"
      ? localStorage.getItem(STORAGE_KEY)
      : null;
  return stored === "en" || stored === "id" ? stored : "id";
}

void i18next.use(initReactI18next).init({
  resources: {
    id: { translation: id },
    en: { translation: en },
  },
  lng: initialLanguage(),
  fallbackLng: "id",
  interpolation: { escapeValue: false },
  returnNull: false,
});

export function setLanguage(language: Language): void {
  localStorage.setItem(STORAGE_KEY, language);
  void i18next.changeLanguage(language);
}

/**
 * Indonesian number formatting: 104.882 for thousands, 0,71 for decimals. Used for
 * COUNTS and SCORES the UI itself computes — never for a figure inside an answer,
 * which renders exactly as the model emitted it because a citation vouches for the
 * digits as written.
 */
export function formatCount(value: number, language: string): string {
  return new Intl.NumberFormat(language === "en" ? "en-GB" : "id-ID").format(value);
}

export default i18next;
